import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Simple mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Card animation variants matching dashboard design - mobile optimized
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Enhanced floating animations - disabled on mobile
const floatingVariants = {
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Mobile-optimized motion component
const MobileOptimizedMotion = ({ children, className, variants, whileHover, transition, initial, animate, exit, ...props }: any) => {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    // Return a regular div on mobile
    return <div className={className}>{children}</div>;
  }
  // Return motion.div on desktop
  return (
    <motion.div 
      className={className}
      variants={variants}
      initial={initial}
      animate={animate}
      exit={exit}
      whileHover={whileHover}
      transition={transition}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Token configuration with proper icons
const TOKEN_CONFIG = {
  XRP: {
    name: 'XRP',
    symbol: 'XRP',
    icon: '/icons/partners/ripple.svg',
    decimals: 6,
    color: 'blue'
  },
  ETH: {
    name: 'Ethereum',
    symbol: 'ETH',
    icon: '/icons/partners/ethereum.svg',
    decimals: 18,
    color: 'purple'
  },
  ARB: {
    name: 'Arbitrum',
    symbol: 'ARB',
    icon: '/icons/partners/arbitrum.svg',
    decimals: 18,
    color: 'blue'
  },
  BNB: {
    name: 'BNB Chain',
    symbol: 'BNB',
    icon: '/icons/partners/BNB.svg',
    decimals: 18,
    color: 'yellow'
  }
};

// Mock exchange rates (XRP as base)
const EXCHANGE_RATES = {
  'XRP-ETH': 0.00014,
  'XRP-ARB': 0.4523,
  'XRP-BNB': 0.00087,
  'ETH-XRP': 7142.86,
  'ARB-XRP': 2.21,
  'BNB-XRP': 1149.43,
};

// Enhanced Token Selection Modal with vault modal reference design
const TokenSelectModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  excludeToken,
  title = "Select Token" 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSelect: (token: string) => void;
  excludeToken?: string;
  title?: string;
}) => {
  const isMobile = useIsMobile();
  
  if (!isOpen) return null;

  const availableTokens = Object.keys(TOKEN_CONFIG).filter(token => token !== excludeToken);

  const modalContent = (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-slate-900/80 backdrop-blur-xl flex items-center justify-center z-[9999] p-4">
      <MobileOptimizedMotion 
        className="rounded-xl p-5 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 w-full max-w-sm"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>
        
        <div className="relative z-10">
          {/* Modal Header */}
          <div className="flex justify-between items-center mb-5">
            <h3 className="text-xl font-semibold text-white">{title}</h3>
            <button 
              onClick={onClose} 
              className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Token List */}
          <div className="space-y-3">
            {availableTokens.map((tokenKey, index) => {
              const token = TOKEN_CONFIG[tokenKey as keyof typeof TOKEN_CONFIG];
              return (
                <MobileOptimizedMotion
                  key={tokenKey}
                  onClick={() => {
                    onSelect(tokenKey);
                    onClose();
                  }}
                  className="w-full p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-blue-500/40 transition-all duration-300 flex items-center gap-4 group cursor-pointer"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.02,
                    boxShadow: "0 0 20px rgba(59, 130, 246, 0.1)"
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="relative">
                    <MobileOptimizedMotion
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/40 flex items-center justify-center"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <img src={token.icon} alt={token.symbol} className="w-6 h-6" />
                    </MobileOptimizedMotion>
                    <motion.div
                      className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100"
                      initial={{ scale: isMobile ? 1 : 0 }}
                      whileHover={isMobile ? {} : { scale: 1 }}
                      transition={isMobile ? {} : { type: "spring", stiffness: 400 }}
                    />
                  </div>
                  <div className="text-left flex-1">
                    <div className="font-semibold text-white text-base">{token.symbol}</div>
                    <div className="text-sm text-slate-400">{token.name}</div>
                  </div>
                  <motion.svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={2} 
                    stroke="currentColor" 
                    className="w-5 h-5 text-slate-400 group-hover:text-blue-400 transition-colors"
                    whileHover={isMobile ? {} : { x: 5 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </motion.svg>
                </MobileOptimizedMotion>
              );
            })}
          </div>
        </div>
      </MobileOptimizedMotion>
    </div>
  );

  return createPortal(modalContent, document.body);
};

const Swap: React.FC = () => {
  const isMobile = useIsMobile();
  const [fromToken, setFromToken] = useState('XRP');
  const [toToken, setToToken] = useState('ETH');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [slippage, setSlippage] = useState(0.1);
  const [showTokenSelect, setShowTokenSelect] = useState<'from' | 'to' | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);
  
  // Mock balances
  const [balances] = useState({
    XRP: 1247.89,
    ETH: 0.5423,
    ARB: 2840.33,
    BNB: 1.2347
  });

  // Calculate exchange rate and amounts
  const getExchangeRate = () => {
    const pair = `${fromToken}-${toToken}`;
    return EXCHANGE_RATES[pair as keyof typeof EXCHANGE_RATES] || 1;
  };

  const handleSwitchTokens = () => {
    const tempToken = fromToken;
    const tempAmount = fromAmount;
    setFromToken(toToken);
    setToToken(tempToken);
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  const handleFromAmountChange = (value: string) => {
    setFromAmount(value);
    if (value && !isNaN(parseFloat(value))) {
      const rate = getExchangeRate();
      const calculated = (parseFloat(value) * rate).toFixed(6);
      setToAmount(calculated);
    } else {
      setToAmount('');
    }
  };

  const handleMaxClick = () => {
    const balance = balances[fromToken as keyof typeof balances];
    const maxAmount = balance.toString();
    handleFromAmountChange(maxAmount);
  };

  const handleSwap = async () => {
    setIsSwapping(true);
    // Simulate swap process
    setTimeout(() => {
      setIsSwapping(false);
      setFromAmount('');
      setToAmount('');
      // In real implementation, update balances here
    }, 2500);
  };

  // Update toAmount when tokens change
  useEffect(() => {
    if (fromAmount) {
      handleFromAmountChange(fromAmount);
    }
  }, [fromToken, toToken]);

  const exchangeRate = getExchangeRate();
  const fromTokenConfig = TOKEN_CONFIG[fromToken as keyof typeof TOKEN_CONFIG];
  const toTokenConfig = TOKEN_CONFIG[toToken as keyof typeof TOKEN_CONFIG];

  return (
    <div className="space-y-6">
      {/* Header Section with Dashboard Theme - Entry animation disabled */}
      <MobileOptimizedMotion 
        className="relative overflow-hidden rounded-xl p-6 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        whileHover={{ 
          scale: 1.01,
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Enhanced animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>
        
        {/* Floating particles - disabled on mobile */}
        <MobileOptimizedMotion className="absolute top-4 left-8 w-2 h-2 bg-blue-400 rounded-full opacity-30" variants={floatingVariants} animate="animate" />
        <MobileOptimizedMotion className="absolute top-12 right-12 w-1 h-1 bg-white rounded-full opacity-20" variants={floatingVariants} animate="animate" style={{ animationDelay: '1s' }} />
        <MobileOptimizedMotion className="absolute bottom-8 left-16 w-1.5 h-1.5 bg-blue-400 rounded-full opacity-25" variants={floatingVariants} animate="animate" style={{ animationDelay: '2s' }} />
        
        <div className="relative z-10">
          <h1 className="text-2xl font-semibold text-white tracking-tight mb-3">
            Token <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Swap</span>
          </h1>
          <p className="text-slate-300 mb-6 max-w-2xl">
            Trade tokens instantly with the best exchange rates across XRP and major networks
          </p>
          
          {/* Stats Grid with Dashboard Design */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              { 
                title: '24H VOLUME', 
                value: '$127.3M', 
                subtitle: 'Cross-chain swaps',
                progress: 85
              },
              { 
                title: 'ACTIVE PAIRS', 
                value: '6', 
                subtitle: 'XRP paired tokens',
                progress: 60
              },
              { 
                title: 'AVG SLIPPAGE', 
                value: '0.15%', 
                subtitle: 'Optimal routing',
                progress: 25
              }
            ].map((item, i) => (
              <MobileOptimizedMotion 
                key={i}
                className="rounded-xl p-4 bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)"
                }}
              >
                <div className="text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">{item.title}</div>
                <div className="text-2xl font-semibold text-white mb-1">{item.value}</div>
                <div className="text-sm text-slate-300 mb-3">{item.subtitle}</div>
                
                {/* Progress bar with dashboard styling - instant on mobile */}
                <div className="h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 h-1.5 rounded-full shadow-lg shadow-blue-500/50"
                    initial={{ width: isMobile ? `${item.progress}%` : 0 }}
                    animate={{ width: `${item.progress}%` }}
                    transition={isMobile ? { duration: 0 } : { duration: 1.5, delay: 0.5 + i * 0.1 }}
                  />
                </div>
              </MobileOptimizedMotion>
            ))}
          </div>
        </div>
      </MobileOptimizedMotion>

      {/* Enhanced Main Swap Interface */}
      <MobileOptimizedMotion 
        className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Enhanced animated background - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>
        
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.3) 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }} />
        </div>

        <div className="relative z-10 p-6">
          {/* Interface Title - Entry animation disabled */}
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-white mb-2">Swap Interface</h2>
            <p className="text-sm text-slate-300">Trade tokens with optimal cross-chain routing</p>
          </div>

          {/* Enhanced Swap Container */}
          <div className="max-w-md mx-auto">
            <div 
              className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 space-y-3 relative"
            >
              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* From Token section - simplified for mobile */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">From</label>
                <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="flex items-center justify-between mb-2 relative z-10">
                    <input
                      type="text"
                      placeholder="0.00"
                      value={fromAmount}
                      onChange={(e) => handleFromAmountChange(e.target.value)}
                      className="flex-1 bg-transparent text-xl font-semibold text-white placeholder-slate-400 border-none outline-none mr-3"
                    />
                    <button
                      onClick={() => setShowTokenSelect('from')}
                      className="flex items-center gap-2 bg-slate-700/50 hover:bg-slate-700/70 rounded-lg px-2 py-2.5 border border-slate-600/50 transition-all duration-300 group min-w-[80px] max-w-[120px] flex-shrink-0 min-h-[44px]"
                    >
                      <div className="relative">
                        <img src={fromTokenConfig.icon} alt={fromTokenConfig.symbol} className="w-5 h-5" />
                      </div>
                      <span className="text-sm font-medium text-white">{fromTokenConfig.symbol}</span>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        strokeWidth={2} 
                        stroke="currentColor" 
                        className="w-3 h-3 text-slate-400 group-hover:text-blue-400 transition-colors"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="flex justify-between text-xs relative z-10">
                    <span className="text-slate-400">
                      Balance: {balances[fromToken as keyof typeof balances].toLocaleString()} {fromTokenConfig.symbol}
                    </span>
                    <button 
                      onClick={handleMaxClick}
                      className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                    >
                      Max
                    </button>
                  </div>
                </div>
              </div>

              {/* Enhanced Swap Direction Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleSwitchTokens}
                  className="w-10 h-10 bg-gradient-to-br from-slate-700/50 to-slate-800/50 hover:from-blue-500/20 hover:to-blue-600/20 rounded-full flex items-center justify-center border border-slate-600/50 hover:border-blue-500/40 transition-all duration-300 group relative"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={2} 
                    stroke="currentColor" 
                    className="w-4 h-4 text-blue-400 group-hover:text-blue-300 transition-colors duration-300 relative z-10"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
                  </svg>
                </button>
              </div>

              {/* To Token section - simplified for mobile */}
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">To</label>
                <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="flex items-center justify-between mb-2 relative z-10">
                    <input
                      type="text"
                      placeholder="0.00"
                      value={toAmount}
                      readOnly
                      className="flex-1 bg-transparent text-xl font-semibold text-white placeholder-slate-400 border-none outline-none cursor-default mr-3"
                    />
                    <button
                      onClick={() => setShowTokenSelect('to')}
                      className="flex items-center gap-2 bg-slate-700/50 hover:bg-slate-700/70 rounded-lg px-2 py-2.5 border border-slate-600/50 transition-all duration-300 group min-w-[80px] max-w-[120px] flex-shrink-0 min-h-[44px]"
                    >
                      <div className="relative">
                        <img src={toTokenConfig.icon} alt={toTokenConfig.symbol} className="w-5 h-5" />
                      </div>
                      <span className="text-sm font-medium text-white">{toTokenConfig.symbol}</span>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        strokeWidth={2} 
                        stroke="currentColor" 
                        className="w-3 h-3 text-slate-400 group-hover:text-blue-400 transition-colors"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="flex justify-between text-xs relative z-10">
                    <span className="text-slate-400">
                      Balance: {balances[toToken as keyof typeof balances].toLocaleString()} {toTokenConfig.symbol}
                    </span>
                  </div>
                </div>
              </div>

              {/* Enhanced Exchange Rate */}
              <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50 relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex justify-between items-center relative z-10">
                  <span className="text-xs text-slate-400">Exchange Rate</span>
                  <span className="text-sm font-medium text-white">
                    1 {fromTokenConfig.symbol} = {exchangeRate.toFixed(6)} {toTokenConfig.symbol}
                  </span>
                </div>
              </div>

              {/* Enhanced Swap Button - simplified for mobile */}
              <button 
                onClick={handleSwap}
                disabled={!fromAmount || isSwapping}
                className={`w-full py-3 px-6 rounded-xl font-medium transition-all duration-300 text-sm relative overflow-hidden group ${
                  !fromAmount || isSwapping
                    ? 'bg-slate-700/50 text-slate-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600 text-white'
                }`}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {isSwapping ? (
                    <>
                      <svg 
                        className={`w-4 h-4 ${!isMobile ? 'animate-spin' : ''}`}
                        fill="none" 
                        viewBox="0 0 24 24"
                      >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="32" strokeDashoffset="32" />
                      </svg>
                      Swapping...
                    </>
                  ) : !fromAmount ? (
                    'Enter Amount'
                  ) : (
                    'Swap Tokens'
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      </MobileOptimizedMotion>

      {/* Recent Swaps Section */}
      <MobileOptimizedMotion 
        className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800/50 via-blue-900/10 to-slate-900/50 backdrop-blur-xl border border-blue-500/20 hover:border-blue-400/40 transition-all duration-500"
        variants={cardVariants}
        whileHover={{ 
          boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)"
        }}
      >
        {/* Animated background pattern - disabled on mobile */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-blue-600/5 ${!isMobile ? 'animate-pulse' : ''}`}></div>

        <div className="relative z-10 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Swaps</h2>
          <div className="space-y-3">
            {[
              { from: 'XRP', to: 'ETH', fromAmount: '5000', toAmount: '0.7142', time: '2 min ago', status: 'Completed' },
              { from: 'ETH', to: 'XRP', fromAmount: '0.25', toAmount: '1785.72', time: '15 min ago', status: 'Completed' },
              { from: 'XRP', to: 'ARB', fromAmount: '1000', toAmount: '452.30', time: '1 hour ago', status: 'Completed' },
              { from: 'BNB', to: 'XRP', fromAmount: '0.5', toAmount: '574.72', time: '3 hours ago', status: 'Completed' }
            ].map((swap, i) => (
              <div 
                key={i} 
                className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 hover:border-blue-500/40 transition-all duration-500"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/40 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-blue-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-white text-sm">
                        {swap.fromAmount} {swap.from} → {swap.toAmount} {swap.to}
                      </div>
                      <div className="text-xs text-slate-300">{swap.time}</div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-medium text-xs px-3 py-1 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                      {swap.status}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </MobileOptimizedMotion>

      {/* Token Selection Modals */}
      <AnimatePresence>
        {showTokenSelect === 'from' && (
          <TokenSelectModal 
            isOpen={showTokenSelect === 'from'}
            onClose={() => setShowTokenSelect(null)}
            onSelect={setFromToken}
            excludeToken={toToken}
            title="Select Token to Swap From"
          />
        )}

        {showTokenSelect === 'to' && (
          <TokenSelectModal 
            isOpen={showTokenSelect === 'to'}
            onClose={() => setShowTokenSelect(null)}
            onSelect={setToToken}
            excludeToken={fromToken}
            title="Select Token to Swap To"
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Swap; 